import React, { useState, useEffect, forwardRef, useRef } from "react";
import styled from "styled-components";
import cx from "classnames";
import useLanguage from '../../utilities/useLanguage';
import { getTranslation } from './translations';
import { getRates } from "../../services/BlackboxService";
import { formatValue } from '../../utilities/formatValue';
import IBuildrProps from "../../utilities/IBuildrProps";
import { InputCurrency, InputDropdown, OptionDropdown } from '../../../';
import Picture from "../Picture";
import { getImagePath } from "../../utilities/path";
import { AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../utilities/automation";

const Container = styled.div`
padding-top: 7rem !important;
padding-bottom: 7rem !important;
.title {
    font-size: 1.5rem;
    font-weight: 350;
    letter-spacing: 0.96px;
    text-transform: uppercase;
}
.monthly-payment {
    font-size: 2.66667rem;
    line-height: 3.078rem;
    @media (min-width:576px){
        font-size: 5.33333rem;
        line-height: 3.078rem;
    }
    font-weight: 350;
}
.monthly-payment-label {
    font-size: 1rem;
    @media (min-width:576px){
        font-size: 1.5rem;
    }
    font-weight: 350;
    letter-spacing: 0.06rem;
    text-transform: uppercase;
}
.cta-button {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem; /* 142.857% */
}
.calc-box {
    z-index: 1;
    max-width: 69rem;
    border-radius: 0.125rem;
    box-shadow: 3px 3px 10px 4px rgba(0, 0, 0, 0.05);
}
.calc-wrapper {
    z-index: 1;
}
.top-left-image, .bottom-right-image {
    width: 18rem;
    height: 18rem;

    @media (min-width:576px){
        width: 28rem;
        height: 28rem;
    }

    @media (min-width:768px){
        width: 34rem;
        height: 34rem;
    }
    img {
        object-fit: contain;
    }
}
.top-left-image,
.bottom-right-image {
    transition: transform 0.5s;
}
.top-left-image {
    transform: translateY(15px);
    top: 0;
    left: -5rem;
    img {
        object-position: top left;
    }
}
.bottom-right-image {
    transform: translateY(15px);
    bottom: 0;
    right: -5rem;
    img {
        object-position: bottom right;
    }
}
.blur, .bg {
    position: absolute;
    height: 100%;
    width: 100%;
}

.bg {
    background: rgba(256,256,256,0.92);
}
.blur {
    -webkit-backdrop-filter: blur(10px) !important;
    backdrop-filter: blur(10px) !important;
    background-color: transparent !important;
    opacity: 1 !important;
}
.child-content{
    min-height: 10rem;
}
`;

export interface IVehiclePaymentCalculatorProps extends IBuildrProps {
    cmsId: string;
    title: string;
    defaultLoanAmount: string;
    rateIds: string;
    ctaText: string;
    ctaLink: string;
    className: string;
    imageLeftSrc: string;
    imageRightSrc: string;
    imageLeftAlt: string;
    imageRightAlt: string;
    children;
}

const VehiclePaymentCalculator = forwardRef(({
    cmsId,
    editorClassObj,
    title,
    defaultLoanAmount,
    rateIds,
    ctaText,
    ctaLink,
    className,
    imageLeftSrc,
    imageRightSrc,
    imageLeftAlt,
    imageRightAlt,
    children,
}: IVehiclePaymentCalculatorProps, ref: any) => {
    const { lang } = useLanguage();
    const wrapperRef = useRef(null); // used for detecting y position of wrapper when page scrolling
    const leftImageRef = useRef(null); // used for setting y position of image
    const rightImageRef = useRef(null); // used for setting y position of image
    const [selectedRate, setSelectedRate] = useState(null);
    const [loanAmount, setLoanAmount] = useState(defaultLoanAmount);
    const [rateData, setRateData] = useState([]);
    const [monthlyPayment, setMonthlyPayment] = useState(0);
    useEffect(() => {
        async function getApiRateData() {
            // structure rateId and superscript information into object array
            let ratesAndSuperscripts = rateIds.split(',').reduce((acc, curr) => {
                let [rateId, superscript] = curr?.split(':');
                rateId = rateId?.trim();
                superscript = superscript?.trim();
                if (rateId) {
                    acc.push({
                        rateId,
                        superscript,
                    });
                }
                return acc;
            }, []);

            // get rate data from api
            const apiRateData = await getRates(ratesAndSuperscripts.map(r => r.rateId));

            // add api rate data to object array
            ratesAndSuperscripts = ratesAndSuperscripts.map(r => {
                return {
                    ...r,
                    apiData: apiRateData.find(apiRate => apiRate.id == r.rateId),
                }
            });

            setRateData(ratesAndSuperscripts);
        }
        getApiRateData();
    }, [rateIds]);

    useEffect(() => {
        if (selectedRate) {
            const rate = selectedRate.apiData;
            const monthlyInterest = Number(selectedRate.apiData.rate.initialRate) / 100 / 12;
            const term = rate.rate.disclosureInformation.termMonths;
            const amount = Number((loanAmount || '0'));
            const x = Math.pow(1 + monthlyInterest, term);
            const monthly = (amount * x * monthlyInterest) / (x - 1);

            setMonthlyPayment(monthly);
        }
    }, [loanAmount, selectedRate]);

    function handleRateChange(selected) {
        setSelectedRate(rateData.find(r => r.rateId == selected));
    }

    function handleScroll() {
        // calculate the y position of the component in the viewport in percentage form.
        // (100% when TOP of component is at BOTTOM of viewport, 0% when BOTTOM of component is at TOP of viewport)
        const yPercentInViewPort = (Number(((wrapperRef.current.getBoundingClientRect().top+(wrapperRef.current.clientHeight))/(window.innerHeight+wrapperRef.current.clientHeight)).toFixed(3)));

        // start: top of image(s) at +15px (y), end: top of images at -15px (y) (30px movement per Cam)
        leftImageRef.current.style.transform = `translateY(${(20) - ((1-yPercentInViewPort)*40)}px)`;
        rightImageRef.current.style.transform = `translateY(${(20) - ((1-yPercentInViewPort)*40)}px)`;
    }

    useEffect(() => {
        const options = {
            threshold: 0,
        };

        const handleIntersection = (entries) => {
            // only one element being observed [0]
            if (entries[0]?.isIntersecting) {
                // only monitor scrolling when element is in viewport
                document.addEventListener('scroll', handleScroll);
            } else {
                document.removeEventListener('scroll', handleScroll);
            }
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        }

        return () => {
            if (wrapperRef.current) {
                observer.unobserve(wrapperRef.current);
            }
        };
    }, []);

    return (
        <div ref={wrapperRef} className="overflow-hidden">
            <Container data-id={cmsId} className={cx(editorClassObj, className, 'container my-6')} ref={ref}>
                <div ref={leftImageRef} className="position-absolute top-left-image">
                    <Picture loading="lazy" src={getImagePath(imageLeftSrc)} alt={imageLeftAlt} className="w-100 h-100 image" />
                </div>
                <div ref={rightImageRef} className="position-absolute bottom-right-image">
                    <Picture loading="lazy" src={getImagePath(imageRightSrc)} alt={imageRightAlt} className="w-100 h-100 image" />
                </div>
                <div className="d-flex flex-column calc-box m-auto flex-grow-1 position-relative">
                    <div className="bg"/>
                    <div className="blur"/>
                    <div className="py-5 px-2 calc-wrapper">
                        <div className="row">
                            <div className="col">
                                <h3 className="title mb-2">{title}</h3>
                            </div>
                        </div>
                        <div className="row">
                            <div className="form flex-grow-1 col-12 col-sm-7 col-md-8 col-lg-4 m-auto">
                            <InputCurrency inputType="currency" inputLabel={getTranslation(lang).loanAmount} onChange={setLoanAmount} inputDefaultValue={defaultLoanAmount} underline />
                                <InputDropdown title={getTranslation(lang).aprAndTerm} onChange={handleRateChange} autoSelect feature={AutomationTestIdFeature.VEHICLE_PAYMENT_CALCULATOR}>
                                    {rateData.map(r => {
                                        return <OptionDropdown eventKey={r.rateId} html={`${formatValue({valueStr: r.apiData.rate.initialRate, formatType: '%', precision: 2 })} - ${r.apiData.termMonths ?? '0'} ${getTranslation(lang).months}${r.superscript ?? ''}`}/>;
                                    })}
                                </InputDropdown>
                                {children ?
                                    <div className='mt-4 child-content'>
                                        {children}
                                    </div>
                                    :
                                    null
                                }
                            </div>
                            <div className="d-flex flex-column flex-grow-1 text-center align-items-center justify-content-center payment-display col-12 col-lg-8">
                                <div className="monthly-payment mb-4">{formatValue({valueStr: `${monthlyPayment}`, precision: 2, formatType: '$'})}</div>
                                <h4 className="monthly-payment-label mb-4">{getTranslation(lang).monthlyPayment}</h4>
                                <a className="cta-button btn btn-primary" data-test-id={createDataTestId({feature: AutomationTestIdFeature.VEHICLE_PAYMENT_CALCULATOR, element: AutomationTestIdElementType.LINK, specifier: 'apply'})} href={ctaLink}>{ctaText}</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
});

export default VehiclePaymentCalculator;